/**
 * 
 * @param needle 
 * @returns an object with the right needles
 * 
 * % Customer
 * ! Eventname
 * @ Location
 * # Veranstaltungsnummer (YYMMDD)
 * $ Angebotsnummer
 */
type TypeSendName = 'needle' |'customerNameNeedle' | 'eventNameNeedle' | 'locationNeedle' | 'eventIdNeedle' | 'offerIdNeedle' | 'greaterThenEventIdNeedle'
type TypeMarker = '%' | '!' | '@' | '#' | '$' | '>'
type TypeMatrix = {
  marker: TypeMarker,
  name: string,
  sendName: TypeSendName,
  modify?: (needle: string) => string,
}
type TypeResult = {
  needle: string,
  customerNameNeedle: string,
  eventNameNeedle: string,
  locationNeedle: string,
  eventIdNeedle: string,
  offerIdNeedle: string,
  greaterThenEventIdNeedle: string,
}

const matrix: TypeMatrix[] = [
  {
    marker: '%',
    name: 'Customer',
    sendName: 'customerNameNeedle'
  },
  {
    marker: '!',
    name: 'Eventname',
    sendName: 'eventNameNeedle'
  },
  {
    marker: '@',
    name: 'Location',
    sendName: 'locationNeedle'
  },
  {
    marker: '#',
    name: 'EventId',
    sendName: 'eventIdNeedle'
  },
  {
    marker: '$',
    name: 'Offer',
    sendName: 'offerIdNeedle'
  },
  {
    marker: '>',
    name: 'EventIdGreaterThen',
    sendName: 'greaterThenEventIdNeedle',
    modify: (n) => {
      let a = ['0','0','0','1','0','1']
      for (let i = 0; i < Math.min(n.length, 6); i++) {
        a[i] = n[i]
      }
      return `20${a[0]}${a[1]}-${a[2]}${a[3]}-${a[4]}${a[5]}`
    }
  }
]

export function NeedleExplorer(needle: string): TypeResult {
  let result: TypeResult = {
    needle: '',
    customerNameNeedle: '',
    eventNameNeedle: '',
    locationNeedle: '',
    eventIdNeedle: '',
    offerIdNeedle: '',
    greaterThenEventIdNeedle: '',
  }
  const splittedNeedle = needle.split(' ')
  let part: string[] = []
  let currentSendName: TypeSendName = 'needle'
  let currentModifier: (needle: string) => string = (n) => n
  splittedNeedle.forEach((n) => {
    const currentMatrixItem = matrix.find(m => n.startsWith(m.marker))
    if (currentMatrixItem) {
      // Qick! Save last value!
      result[currentSendName] = currentModifier(part.join(' '))
      // start with new part
      currentModifier = currentMatrixItem.modify ? currentMatrixItem.modify : (n) => n
      part = [n.slice(1)]
      currentSendName = currentMatrixItem.sendName
    } else {
      part.push(n)
    }
  })
  
  result[currentSendName] = currentModifier(part.join(' '))
  return result
}


/**
 * This function tests the NeedleExplorer with some testcases.
 *
 * It will take a list of subjects, apply the NeedleExplorer and compare the result with the expected result.
 *
 * The testcases are:
 * - Only the needle
 * - Needle and customer
 * - Needle and customer with a space
 * - Needle, customer, eventid and location
 *
 * If the result does not match the expected result, an error will be thrown.
 */
function testNeedleExplorer() {
  const subjects: {
    needle: string,
    result: TypeResult
  }[] = [
    {
      needle: 'Hugo Gehrmann',
      result: {
        needle: 'Hugo Gehrmann',
        customerNameNeedle: '',
        eventNameNeedle: '',
        locationNeedle: '',
        eventIdNeedle: '',
        offerIdNeedle: '',
        greaterThenEventIdNeedle: '',
      }
    },
    {
      needle: 'Hugo Gehrmann %Firma',
      result: {
        needle: 'Hugo Gehrmann',
        customerNameNeedle: 'Firma',
        eventNameNeedle: '',
        locationNeedle: '',
        eventIdNeedle: '',
        offerIdNeedle: '',
        greaterThenEventIdNeedle: '',
      }
    },
    {
      needle: 'Hugo Gehrmann %Firma Krause',
      result: {
        needle: 'Hugo Gehrmann',
        customerNameNeedle: 'Firma Krause',
        eventNameNeedle: '',
        locationNeedle: '',
        eventIdNeedle: '',
        offerIdNeedle: '',
        greaterThenEventIdNeedle: '',
      }
    },
    {
      needle: 'Hugo Gehrmann #4242 %Firma Krause @Frankfurt am Main',
      result: {
        needle: 'Hugo Gehrmann',
        customerNameNeedle: 'Firma Krause',
        eventNameNeedle: '',
        locationNeedle: 'Frankfurt am Main',
        eventIdNeedle: '4242',
        offerIdNeedle: '',
        greaterThenEventIdNeedle: '',
      }
    },
    {
      needle: 'Hugo Gehrmann #4242 >130524 %Firma Krause @Frankfurt am Main',
      result: {
        needle: 'Hugo Gehrmann',
        customerNameNeedle: 'Firma Krause',
        eventNameNeedle: '',
        locationNeedle: 'Frankfurt am Main',
        eventIdNeedle: '4242',
        offerIdNeedle: '',
        greaterThenEventIdNeedle: '130524',
      }
    },
  ]
  subjects.forEach(subject => {
    const result = NeedleExplorer(subject.needle)
    const testKeys: TypeSendName[] = ['needle', 'customerNameNeedle', 'eventNameNeedle', 'locationNeedle', 'eventIdNeedle', 'offerIdNeedle']
    testKeys.forEach(key => {
      if (result[key] !== subject.result[key]) {
        console.log(`NeedleExplorer Test failed. OrigNeedle: "${subject.needle}", key: "${key}" ? "${result[key]}" != "${subject.result[key]}", ${result[key] === subject.result[key]}`)
      }
    })
  })
}

testNeedleExplorer()
console.log('NeedleExplorer selftest done!')
